import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Clock, MessageSquare, Download, Trash2, X, Sparkles, File } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { documentsApi, opinionSessionsApi, OpinionSessionListItem } from '../services/api'
import PdfViewer from '../components/shared/PdfViewer'

const DOC_TYPE_LABELS: Record<string, string> = {
  OpiniaPrawna: 'Opinia',
  Pozew: 'Pozew',
  Umowa: 'Umowa',
  PismoProcesowe: 'Pismo procesowe',
  Wezwanie: 'Wezwanie',
  Odwolanie: 'Odwołanie',
  Skarga: 'Skarga',
  Wniosek: 'Wniosek',
  Notatka: 'Notatka',
  OdpowiedzNaPozew: 'Odpowiedź na pozew',
  DodatkoweInformacje: 'Dodatkowe Informacje',
}

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const { data: document, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsApi.get(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: versions } = useQuery({
    queryKey: ['document-versions', id],
    queryFn: () => documentsApi.getVersions(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: comments } = useQuery({
    queryKey: ['document-comments', id],
    queryFn: () => documentsApi.getComments(id!).then(r => r.data),
    enabled: !!id,
  })

  // Find opinion session linked to this document (if it was generated from one)
  const { data: linkedSession } = useQuery({
    queryKey: ['document-linked-session', document?.projectId, id],
    queryFn: async () => {
      const sessions = await opinionSessionsApi.list(document!.projectId).then(r => r.data)
      return sessions.find((s: OpinionSessionListItem) => s.resultDocumentId === id) || null
    },
    enabled: !!document?.projectId && !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => documentsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/documents')
    },
  })

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  const handleExport = async () => {
    if (!id || isExporting) return

    setIsExporting(true)
    try {
      // If document has an original file, download it directly
      if (document?.hasOriginalFile) {
        const response = await documentsApi.getFile(id, true)
        const mimeType = document.mimeType || 'application/octet-stream'
        const blob = new Blob([response.data], { type: mimeType })
        const url = window.URL.createObjectURL(blob)
        const link = window.document.createElement('a')
        link.href = url
        const mimeToExt: Record<string, string> = {
          'application/pdf': '.pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
          'application/msword': '.doc',
          'text/plain': '.txt',
          'application/rtf': '.rtf',
          'application/vnd.oasis.opendocument.text': '.odt',
          'image/jpeg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/bmp': '.bmp',
          'image/tiff': '.tiff',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          'application/vnd.ms-excel': '.xls',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        }
        const ext = mimeToExt[mimeType] || ''
        link.download = `${document.title || 'dokument'}${ext}`
        window.document.body.appendChild(link)
        link.click()
        window.document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        // AI-generated or text-only: export as DOCX
        const response = await documentsApi.export(id)
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        })
        const url = window.URL.createObjectURL(blob)
        const link = window.document.createElement('a')
        link.href = url
        link.download = `${document?.title || 'dokument'}.docx`
        window.document.body.appendChild(link)
        link.click()
        window.document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!document) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Dokument nie został znaleziony</p>
      </div>
    )
  }

  const isPdf = document.hasOriginalFile && document.mimeType === 'application/pdf'

  // Shared header card
  const headerCard = (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{document.title}</h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            Typ: {DOC_TYPE_LABELS[document.type] ?? document.type}
            {document.sourcesUsed && (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                <Sparkles className="w-3 h-3" />
                AI
              </span>
            )}
            <span>• Wersja {document.currentVersion}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary disabled:opacity-50"
          >
            <Download className={`w-4 h-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
            {isExporting ? 'Pobieranie...' : document.hasOriginalFile ? 'Pobierz oryginal' : 'Eksportuj'}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn-secondary text-red-600 hover:bg-red-50 hover:border-red-300"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Usuń
          </button>
        </div>
      </div>
    </div>
  )

  // Delete confirmation modal
  const deleteModal = showDeleteModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-600 bg-opacity-50"
        onClick={() => setShowDeleteModal(false)}
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-red-600">Usuń dokument</h2>
          <button
            onClick={() => setShowDeleteModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Czy na pewno chcesz usunąć dokument <strong>"{document.title}"</strong>?
          Ta operacja jest nieodwracalna.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="btn-secondary"
          >
            Anuluj
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Usuwanie...' : 'Usuń'}
          </button>
        </div>
      </div>
    </div>
  )

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Metadata */}
      <div className="card p-4">
        <h3 className="font-semibold mb-4">Informacje</h3>
        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-gray-500">Utworzono</dt>
            <dd>{new Date(document.createdAt).toLocaleDateString('pl-PL')}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Ostatnia modyfikacja</dt>
            <dd>{new Date(document.updatedAt).toLocaleDateString('pl-PL')}</dd>
          </div>
          {document.caseId && (
            <div>
              <dt className="text-gray-500">Sprawa</dt>
              <dd>{document.caseId}</dd>
            </div>
          )}
          {document.legalStateDate && (
            <div>
              <dt className="text-gray-500">Stan prawny na</dt>
              <dd>{new Date(document.legalStateDate).toLocaleDateString('pl-PL')}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Generation History Link */}
      {linkedSession && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3 flex items-center">
            <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
            Historia generacji
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Ten dokument zostal wygenerowany w sesji opinii z {linkedSession.currentVersion} {linkedSession.currentVersion === 1 ? 'wersja' : linkedSession.currentVersion < 5 ? 'wersjami' : 'wersjami'}.
          </p>
          <button
            onClick={() => navigate(`/generate?sessionId=${linkedSession.id}`)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Zobacz pelna historie
          </button>
        </div>
      )}

      {/* Versions */}
      <div className="card p-4">
        <h3 className="font-semibold mb-4 flex items-center">
          <Clock className="w-4 h-4 mr-2" />
          Historia wersji
        </h3>
        <div className="space-y-2">
          {versions?.slice(0, 5).map((v) => (
            <div key={v.id} className="text-sm p-2 rounded hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="font-medium">Wersja {v.version}</span>
                <span className="text-gray-500 text-xs">
                  {new Date(v.createdAt).toLocaleDateString('pl-PL')}
                </span>
              </div>
              {v.comment && (
                <p className="text-gray-500 mt-1">{v.comment}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Comments */}
      <div className="card p-4">
        <h3 className="font-semibold mb-4 flex items-center">
          <MessageSquare className="w-4 h-4 mr-2" />
          Komentarze ({comments?.length || 0})
        </h3>
        <div className="space-y-3">
          {comments?.slice(0, 3).map((c) => (
            <div key={c.id} className="text-sm border-l-2 border-gray-200 pl-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{c.userName}</span>
                <span className="text-gray-500 text-xs">
                  {new Date(c.createdAt).toLocaleDateString('pl-PL')}
                </span>
              </div>
              <p className="text-gray-600 mt-1">{c.content}</p>
            </div>
          ))}
          {!comments?.length && (
            <p className="text-gray-500 text-sm">Brak komentarzy</p>
          )}
        </div>
      </div>
    </>
  )

  // PDF layout: full-width viewer with compact metadata bar
  if (isPdf) {
    return (
      <div className="w-full space-y-4">
        {headerCard}
        {deleteModal}

        {/* Compact metadata bar */}
        <div className="card p-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-500">
            <span>Utworzono: <strong className="text-gray-900">{new Date(document.createdAt).toLocaleDateString('pl-PL')}</strong></span>
            <span>Modyfikacja: <strong className="text-gray-900">{new Date(document.updatedAt).toLocaleDateString('pl-PL')}</strong></span>
            {document.legalStateDate && (
              <span>Stan prawny: <strong className="text-gray-900">{new Date(document.legalStateDate).toLocaleDateString('pl-PL')}</strong></span>
            )}
            {document.caseId && (
              <span>Sprawa: <strong className="text-gray-900">{document.caseId}</strong></span>
            )}
            <span>Wersja: <strong className="text-gray-900">{document.currentVersion}</strong></span>
          </div>
        </div>

        {/* Full-width PDF viewer */}
        <PdfViewer documentId={document.id} title={document.title} />
      </div>
    )
  }

  // Default layout: 2/3 content + 1/3 sidebar
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main content */}
      <div className="lg:col-span-2 space-y-6">
        {headerCard}
        {deleteModal}

        {/* Content */}
        {document.hasOriginalFile ? (
          /* Non-PDF attached file: download banner + extracted text */
          <>
            <div className="card p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <File className="w-8 h-8 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Oryginalny plik ({formatFileSize(document.fileSize)})
                  </p>
                  <p className="text-xs text-gray-500">{document.mimeType}</p>
                </div>
              </div>
              <button onClick={handleExport} className="btn-secondary text-sm">
                <Download className="w-4 h-4 mr-1.5" />
                Pobierz oryginal
              </button>
            </div>
            <div className="card p-6">
              <div className="prose max-w-none">
                <ReactMarkdown>{document.content}</ReactMarkdown>
              </div>
            </div>
          </>
        ) : (
          /* AI-generated or text-only: render Markdown */
          <div className="card p-6">
            <div className="prose max-w-none">
              <ReactMarkdown>{document.content}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {sidebarContent}
      </div>
    </div>
  )
}
